import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Upload, X, FileText, Image as ImageIcon, Video } from "lucide-react";
import { toast } from "sonner";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import ImageUploadSlots from "./ImageUploadSlots";
import TherapeuticCategorySelector from "./TherapeuticCategorySelector";
import ProductMetadataFields from "./ProductMetadataFields";

interface ProductFormData {
  brandName: string;
  composition: string;
  type: string;
  therapeuticCategory: string[];
  packagingType: string;
  price: string;
  mrp: string;
  youtubeLink: string;
  images: string[];
  visualPageId: string;
  productPdfId: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  metaRobots: string;
  metaAuthor: string;
  metaCopyright: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterCard: string;
  favicon: string;
  isExclusive: boolean;
  isNowInStock: boolean;
}

interface ProductFormProps {
  editingId: Id<"products"> | null;
  initialData: ProductFormData;
  onCancel: () => void;
  onSuccess: () => void;
}

const productTypes = [
  "Tablets", "Capsules", "Softgels", "Dental & Gargle (Mouthwash)",
  "Paediatric", "Syrup", "Spray", "Dusting Powder", "Ear & Eye Drop",
  "Nasal Spray", "Powder & Sachet", "Infusions", "Rota Caps", "Ointments",
  "Others",
];

export default function ProductForm({ editingId, initialData, onCancel, onSuccess }: ProductFormProps) {
  const [formData, setFormData] = useState<ProductFormData>(initialData);
  
  const createProduct = useMutation((api as any).products.createProduct);
  const updateProduct = useMutation((api as any).products.updateProduct);
  const generateUploadUrl = useMutation((api as any).products.generateUploadUrl);

  const handleFileUpload = async (file: File, field: "visualPageId" | "productPdfId") => {
    try {
      const uploadUrl = await generateUploadUrl();
      const result = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file,
      });
      const { storageId } = await result.json();
      setFormData({ ...formData, [field]: storageId });
      toast.success("File uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload file");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const productData = {
        brandName: formData.brandName,
        composition: formData.composition,
        type: formData.type,
        therapeuticCategory: formData.therapeuticCategory.length > 0 ? formData.therapeuticCategory : undefined,
        packagingType: formData.packagingType,
        price: formData.price || undefined,
        mrp: formData.mrp || undefined,
        youtubeLink: formData.youtubeLink || undefined,
        images: formData.images.filter(img => img),
        visualPageId: formData.visualPageId || undefined,
        productPdfId: formData.productPdfId || undefined,
        metaTitle: formData.metaTitle || undefined,
        metaDescription: formData.metaDescription || undefined,
        metaKeywords: formData.metaKeywords || undefined,
        metaRobots: formData.metaRobots || undefined,
        metaAuthor: formData.metaAuthor || undefined,
        metaCopyright: formData.metaCopyright || undefined,
        ogTitle: formData.ogTitle || undefined,
        ogDescription: formData.ogDescription || undefined,
        ogImage: formData.ogImage || undefined,
        twitterCard: formData.twitterCard || undefined,
        favicon: formData.favicon || undefined,
        isExclusive: formData.isExclusive,
        isNowInStock: formData.isNowInStock,
      };

      if (editingId) {
        await updateProduct({ id: editingId, ...productData });
        toast.success("Product updated successfully");
      } else {
        await createProduct(productData);
        toast.success("Product created successfully");
      }

      onSuccess();
    } catch (error) {
      toast.error("Failed to save product");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="outline" size="icon" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-4xl font-bold">{editingId ? "Edit Product" : "Create New Product"}</h1>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <ImageUploadSlots 
              images={formData.images}
              onChange={(images) => setFormData({ ...formData, images })}
            />

            {/* Visual Page Upload */}
            <div className="space-y-2">
              <Label>Visual Page</Label>
              <div className="border-2 border-dashed rounded-lg p-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Upload className="h-5 w-5" />
                  <span>{formData.visualPageId ? "Visual page uploaded" : "Upload visual page"}</span>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file, "visualPageId");
                    }}
                  />
                </label>
              </div>
            </div>

            {/* Product PDF Upload */}
            <div className="space-y-2">
              <Label>Product PDF</Label>
              <div className="border-2 border-dashed rounded-lg p-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <FileText className="h-5 w-5" />
                  <span>{formData.productPdfId ? "PDF uploaded" : "Upload product PDF"}</span>
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file, "productPdfId");
                    }}
                  />
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="brandName">Brand Name *</Label>
              <Input
                id="brandName"
                value={formData.brandName}
                onChange={(e) => setFormData({ ...formData, brandName: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="composition">Composition *</Label>
              <Textarea
                id="composition"
                value={formData.composition}
                onChange={(e) => setFormData({ ...formData, composition: e.target.value })}
                rows={3}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type *</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select product type" />
                </SelectTrigger>
                <SelectContent>
                  {productTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <TherapeuticCategorySelector
              selected={formData.therapeuticCategory}
              onChange={(categories) => setFormData({ ...formData, therapeuticCategory: categories })}
            />

            <div className="space-y-2">
              <Label htmlFor="packagingType">Packaging Type *</Label>
              <Input
                id="packagingType"
                value={formData.packagingType}
                onChange={(e) => setFormData({ ...formData, packagingType: e.target.value })}
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Price</Label>
                <Input
                  id="price"
                  type="text"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="e.g., ₹299 or Contact for pricing"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mrp">MRP</Label>
                <Input
                  id="mrp"
                  type="text"
                  value={formData.mrp}
                  onChange={(e) => setFormData({ ...formData, mrp: e.target.value })}
                  placeholder="e.g., ₹399 or N/A"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="youtubeLink">YouTube Video Link</Label>
              <div className="flex gap-2">
                <Video className="h-5 w-5 mt-2 text-muted-foreground" />
                <Input
                  id="youtubeLink"
                  type="url"
                  value={formData.youtubeLink}
                  onChange={(e) => setFormData({ ...formData, youtubeLink: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 p-4 border rounded-lg bg-muted/20">
              <div className="flex items-center justify-between space-x-2">
                <Label htmlFor="isExclusive" className="flex flex-col space-y-1">
                  <span>Exclusive Product</span>
                  <span className="font-normal text-xs text-muted-foreground">Show in 'Our Exclusive Products' section</span>
                </Label>
                <Switch
                  id="isExclusive"
                  checked={formData.isExclusive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isExclusive: checked })}
                />
              </div>
              <div className="flex items-center justify-between space-x-2">
                <Label htmlFor="isNowInStock" className="flex flex-col space-y-1">
                  <span>Now in Stock</span>
                  <span className="font-normal text-xs text-muted-foreground">Show in 'Now in Stock' section</span>
                </Label>
                <Switch
                  id="isNowInStock"
                  checked={formData.isNowInStock}
                  onCheckedChange={(checked) => setFormData({ ...formData, isNowInStock: checked })}
                />
              </div>
            </div>

            <ProductMetadataFields
              formData={formData}
              onChange={(updates) => setFormData({ ...formData, ...updates })}
            />

            <div className="flex gap-4 pt-4">
              <Button type="submit" className="flex-1">
                {editingId ? "Update Product" : "Create Product"}
              </Button>
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}